import { useMemo } from "react";
import { CHART_DEFAULTS, ChartDimensions } from "@/types/chart";
import { ReportChartSettingsV2 } from "@/common/report-configuration";

export const useChartDimensionsV2 = (
  chartOptions: ReportChartSettingsV2,
  maxHeight?: number,
  containerWidth?: number,
): ChartDimensions => {
  return useMemo(() => {
    const defaultWidth = 800;
    const defaultHeight = 400;

    // Calculate actual dimensions
    const actualWidth = containerWidth || defaultWidth;
    const actualHeight = Math.min(maxHeight || Infinity, defaultHeight);

    // Calculate base dimensions without legend adjustments
    const baseChartHeight = Math.max(
      600,
      actualHeight - CHART_DEFAULTS.MARGIN.top - CHART_DEFAULTS.MARGIN.bottom,
    );

    // For bottom legend, we want the chart to be taller but not trigger a resize loop
    const finalChartHeight =
      chartOptions.legendPosition === "bottom"
        ? Math.min(
            baseChartHeight + 100,
            (maxHeight || actualHeight) - CHART_DEFAULTS.LEGEND_HEIGHT,
          )
        : baseChartHeight;

    // Only subtract legend width for left/right positions
    const legendWidthAdjustment =
      chartOptions.includeLegend &&
      ["left", "right"].includes(chartOptions.legendPosition || "right")
        ? CHART_DEFAULTS.LEGEND_WIDTH
        : 0;

    return {
      margin: CHART_DEFAULTS.MARGIN,
      chartWidth: Math.max(
        200,
        actualWidth -
          CHART_DEFAULTS.MARGIN.left -
          CHART_DEFAULTS.MARGIN.right -
          legendWidthAdjustment,
      ),
      chartHeight: finalChartHeight,
      width: actualWidth,
      height: actualHeight,
      fullHeight: actualHeight,
    };
  }, [
    maxHeight,
    containerWidth,
    chartOptions.includeLegend,
    chartOptions.legendPosition,
  ]);
};
